import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import Iconify from 'src/components/iconify';
import { varFade, MotionViewport } from 'src/components/animate';

// ----------------------------------------------------------------------

const CARDS = [
  {
    icon: 'solar:chart-2-bold-duotone',
    color: 'primary',
    title: 'Skill Tracking',
    description:
      'Track progress across skills, topics, and subtopics with clear completion at every level.',
  },
  {
    icon: 'solar:book-bookmark-bold-duotone',
    color: 'info',
    title: 'Skill Builder & Library',
    description: 'Build your own skills and a knowledge library your team actually uses.',
  },
  {
    icon: 'solar:pie-chart-2-bold-duotone',
    color: 'warning',
    title: 'Analytics & Gaps',
    description: 'Spot skill gaps, top skills, and job-role coverage at a glance.',
  },
] as const;

// ----------------------------------------------------------------------

export default function HomeMinimal() {
  return (
    <Container
      component={MotionViewport}
      sx={{
        py: { xs: 10, md: 15 },
      }}
    >
      <Stack spacing={2} sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
        <m.div variants={varFade().inDown}>
          <Typography
            component="div"
            variant="overline"
            sx={{ color: 'primary.main', letterSpacing: 1.2 }}
          >
            Why TechStack
          </Typography>
        </m.div>

        <m.div variants={varFade().inDown}>
          <Typography variant="h2">
            How TechStack <br />
            helps you?
          </Typography>
        </m.div>
      </Stack>

      <Box
        gap={{ xs: 3, md: 4 }}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          md: 'repeat(3, 1fr)',
        }}
      >
        {CARDS.map((card) => (
          <m.div variants={varFade().inUp} key={card.title}>
            <Card
              sx={{
                height: 1,
                textAlign: 'center',
                p: { xs: 4, md: 5 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.12)}`,
                boxShadow: (theme) =>
                  `0 12px 32px -12px ${alpha(theme.palette.grey[500], 0.16)}`,
                transition: (theme) =>
                  theme.transitions.create(['transform', 'box-shadow', 'border-color'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                '&:hover': {
                  transform: 'translateY(-8px)',
                  borderColor: (theme) => alpha(theme.palette[card.color].main, 0.4),
                  boxShadow: (theme) =>
                    `0 24px 48px -16px ${alpha(theme.palette[card.color].main, 0.28)}`,
                },
              }}
            >
              <Box
                sx={{
                  mx: 'auto',
                  mb: 3,
                  width: 72,
                  height: 72,
                  display: 'flex',
                  borderRadius: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: `${card.color}.main`,
                  bgcolor: (theme) => alpha(theme.palette[card.color].main, 0.1),
                }}
              >
                <Iconify icon={card.icon} width={40} />
              </Box>

              <Typography variant="h5" sx={{ mb: 1.5 }}>
                {card.title}
              </Typography>

              <Typography sx={{ color: 'text.secondary' }}>{card.description}</Typography>
            </Card>
          </m.div>
        ))}
      </Box>
    </Container>
  );
}
